import { useMemo } from 'react';
import { useIntl } from '@leagueplatform/locales';
import type { GDSStyleObject } from '@leagueplatform/genesis-core';
import type {
  UserMenuItem,
  UserMenuSection,
} from '@leagueplatform/solutions-foundation';
import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { EXTERNAL_LINKS, INTERNAL_LINKS } from 'common/links';
import { useGetCaregiverStatus } from 'hooks/use-get-caregiver-status.hook';
import { QUERY_PARAM_MODAL_VALUE, QUERY_PARAM_KEYS } from 'common/constants';
import { useUrlParam } from 'hooks/use-url-param.hook';

// !important overrides the &&-wrapped base styles in MobileNavLink
const MENU_LINK_CSS: GDSStyleObject = {
  typography: '$buttonTwo !important',
  color: '$onSurfaceTextPrimary !important',
} as unknown as GDSStyleObject;

export const useUserMenuConfig = (): { userMenu: UserMenuSection[] } => {
  const { formatMessage } = useIntl();
  const { data: isCaregiver, isImpersonationStarted } = useGetCaregiverStatus();
  const { setParam } = useUrlParam(QUERY_PARAM_KEYS.MODAL);

  const userMenu = useMemo(
    (): UserMenuSection[] => [
      {
        heading: formatMessage({ id: 'STR_ACCOUNT' }),
        items: [
          {
            label: formatMessage({ id: 'STR_PERSONAL_INFO' }),
            to: INTERNAL_LINKS.PERSONAL_INFO,
            css: MENU_LINK_CSS,
          },
          !isCaregiver && {
            label: formatMessage({ id: 'STR_MY_CAREGIVERS' }),
            to: getModulePath(LEAGUE_MODULE_NAMES.caregiver),
            css: MENU_LINK_CSS,
          },
          {
            label: formatMessage({ id: 'STR_SHARING_YOUR_DATA' }),
            to: EXTERNAL_LINKS.SHARING_YOUR_DATA,
            target: '_blank',
            css: MENU_LINK_CSS,
          },
          {
            label: formatMessage({ id: 'STR_SECURITY_PREFERENCES' }),
            to: EXTERNAL_LINKS.SECURITY_PREFERENCES,
            target: '_blank',
            css: MENU_LINK_CSS,
          },
        ].filter(Boolean) as UserMenuItem[],
      },
      {
        heading: formatMessage({ id: 'STR_ABOUT' }),
        items: [
          {
            label: formatMessage({ id: 'STR_ABOUT_TRILLIUM_CONNECT' }),
            to: INTERNAL_LINKS.ABOUT,
            css: MENU_LINK_CSS,
          },
          {
            label: formatMessage({ id: 'STR_PRIVACY_POLICY' }),
            to: EXTERNAL_LINKS.PRIVACY_POLICY, // temporary URL — confirm final with THP Legal (OQ-ONB-04)
            target: '_blank',
            css: MENU_LINK_CSS,
          },
          {
            label: formatMessage({ id: 'STR_TERMS_OF_SERVICE' }),
            to: EXTERNAL_LINKS.TERMS_OF_SERVICE, // temporary URL — confirm final with THP Legal (OQ-ONB-04)
            target: '_blank',
            css: MENU_LINK_CSS,
          },
          !isImpersonationStarted && {
            label: formatMessage({ id: 'STR_DELETE_YOUR_ACCOUNT' }),
            onClick: () => setParam(QUERY_PARAM_MODAL_VALUE.DELETE_ACCOUNT),
            css: MENU_LINK_CSS,
          },
        ].filter(Boolean) as UserMenuItem[],
      },
      {
        items: [
          {
            label: formatMessage({ id: 'STR_LOG_OUT' }),
            to: APP_MANAGED_ROUTES.SIGN_OUT,
            variant: 'critical',
          },
        ],
      },
    ],
    [isCaregiver, isImpersonationStarted, formatMessage, setParam],
  );

  return { userMenu };
};
